import React, { useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Grid2,
} from "@mui/material";
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import ChartCalendar from "../../DigitalTwinV3/ChartCalendar";
import { PrimaryGranularityButton } from "../../../Theme/DigitalTwinStyles";
import LocationFilter from "./DetailsFilters/LocationFilter";
import GlobalFilter from "../../Overview/components/GlobalFilter";
import OperationalStatusFilter from "../../Common/OperationalStatusFilter";
import { useCanUseGlobalAdvancedFilter } from "../../../Helper/Permissions/GlobalAdvancedFilter";
import AppCache from "../../../Helper/Common/AppCache";
import ProjectFilter from "./DetailsFilters/ProjectFilter";
import FaultCategoryFilter from "./DetailsFilters/FaultCategoryFilter";
import FaultNameFilter from "./DetailsFilters/FaultNameFilter";
import EntityAffectedFilter from "./DetailsFilters/EntityAffectedFilter";
import ResolutionDurationFilter from "./DetailsFilters/ResolutionDurationFilter";
import CustomIcon from "../../../Components/CustomIcon";
import { FilterIconSvg } from "../../../Features/Table/Icons";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { analyticsCalendarEndDate } from "../../../Helper/DatePicker/DateConverter";
import { CHART_TYPES } from "./chartConfig";

const ChartDetailsSettingsSidebar = ({
  isExpanded,
  onToggle,
  chartType,
  startDate,
  endDate,
  onDateChange,
  granularity,
  onGranularityChange,
  allFleets,
  allOnboardingLocationsHierarchy,
  selectedFleetIds,
  selectedHierarchicalLocation,
  initialAlertCategories,
  initialOperationalStatus,
  onFiltersChange,
  onHierarchicalLocationChange,
  onFleetIdsChange,
  onAlertCategoriesChange,
  onOperationalStatusChange
}) => {
  const canUseGlobalAdvancedFilter = useCanUseGlobalAdvancedFilter();
  const globalFilterConfig = AppCache.getItem("globalFilter")?.find((f) => f.type === "unifiedGlobalFilter") || null;
  const [activeTab, setActiveTab] = useState(0);

  // Store initial/original filters passed from parent
  const initialFiltersRef = React.useRef({
    hierarchicalLocation: selectedHierarchicalLocation || [],
    fleetIds: selectedFleetIds || [],
    alertCategories: initialAlertCategories || [],
    operationalStatus: initialOperationalStatus || []
  });

  // Filter states
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedOperationalStatus, setSelectedOperationalStatus] = useState(initialOperationalStatus || []);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(initialAlertCategories || []);
  const [selectedFaultNames, setSelectedFaultNames] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [selectedResolutionDurations, setSelectedResolutionDurations] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  // Reset all filters to original state
  const handleResetFilters = () => {
    // Reset all locally updated filters
    setSelectedProjects([]);
    setSelectedFaultNames([]);
    setSelectedEntities([]);
    setSelectedResolutionDurations([]);

    // Reset to original filters from parent
    setSelectedCategories(initialFiltersRef.current.alertCategories);
    setSelectedOperationalStatus(initialFiltersRef.current.operationalStatus);

    // Notify parent to reset hierarchical location
    if (onHierarchicalLocationChange) {
      onHierarchicalLocationChange(initialFiltersRef.current.hierarchicalLocation);
    }

    // Notify parent to reset the customer scope (business segment + customer
    // account). The parent seeds its local value from the same page scope this
    // ref captured, so restoring it is a no-op unless the user changed it.
    if (onFleetIdsChange) {
      onFleetIdsChange(initialFiltersRef.current.fleetIds);
    }

    // Notify parent to reset alert categories
    if (onAlertCategoriesChange) {
      onAlertCategoriesChange(initialFiltersRef.current.alertCategories);
    }

    // Notify parent to reset operational status
    if (onOperationalStatusChange) {
      onOperationalStatusChange(initialFiltersRef.current.operationalStatus);
    }
  };

  // Build and apply filters whenever any filter's Apply button is clicked
  React.useEffect(() => {
    const filters = [];

    // Add Project filter (configName)
    if (selectedProjects.length > 0) {
      filters.push({
        name: "configName",
        values: selectedProjects
      });
    }

    // NOTE: Fault Category filter is NOT added here because it's already sent via headers (bmsAlertCategories)

    // Add Fault Name filter (errorDescription)
    if (selectedFaultNames.length > 0) {
      filters.push({
        name: "errorDescription",
        values: selectedFaultNames
      });
    }

    // Add Entity Affected filter (entityAffected)
    if (selectedEntities.length > 0) {
      filters.push({
        name: "hierarchy",
        values: selectedEntities
      });
    }

    // Add Resolution Duration filter (resolutionDuration)
    if (selectedResolutionDurations.length > 0) {
      filters.push({
        name: "resolutionDuration",
        values: selectedResolutionDurations
      });
    }

    setAppliedFilters(filters);

    // Notify parent component about filter changes
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [selectedProjects, selectedFaultNames, selectedEntities, selectedResolutionDurations, onFiltersChange]);

  const granularityOptions = ["Daily", "Weekly", "Monthly"];

  return (
    <>
      {/* Settings Icon Button - Always visible */}
      <IconButton
        onClick={onToggle}
        sx={{
          position: "absolute",
          right: isExpanded ? "320px" : "0",
          zIndex: 1000,
          backgroundColor: "transparent",
          width: "20px",
          height: "20px",
          transition: "right 0.3s ease-in-out",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&:focus": {
            backgroundColor: "transparent",
          },
        }}
      >
        <PrimaryTooltip
          title={isExpanded ? 'Collapse Panel' : 'Expand Panel'}
          placement="bottom"
        >
          <KeyboardTabIcon sx={{ fontSize: 20, color: "#494949", transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)', '&:hover': { color: "#6D72F6" } }} />
        </PrimaryTooltip>
      </IconButton>

      <Box
        sx={{
          width: isExpanded ? "320px" : "0px",
          transition: "width 0.3s ease-in-out",
          borderLeft: isExpanded ? "1px solid #E6E6E6" : "none",
          backgroundColor: "#FFFFFF",
          height: "100%",
          overflow: "hidden",
          flexShrink: 0,
          my: -2,
          mr: -3,
          position: 'fixed',
          right: '24px',
        }}
      >
        {/* Sidebar Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "320px",
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          {/* Header with Tabs */}
          <Box sx={{ px: 3, flexShrink: 0 }}>
            <Box sx={{ mx: -3 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  minHeight: "40px",
                  "& .MuiTab-root": {
                    minHeight: "40px",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#8D8D8D",
                    p: 2,
                    "&.Mui-selected": {
                      color: "#494949",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#494949",
                  },
                }}
              >
                <Tab label="Settings" />
              </Tabs>
            </Box>
            <Divider sx={{ borderColor: "#E6E6E6", mt: 0, mx: -3 }} />
          </Box>

          {/* Scrollable Content Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "scroll",
              pb: 3,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
            }}
          >
            {activeTab === 0 && (
              <Box>
                <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: "none",
                    borderBottom: '1px solid #E6E6E6',
                    "&:before": { display: "none" },
                    '&.Mui-expanded': {
                      mt: 0,
                      mb: 0
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardDoubleArrowDownIcon />}
                    sx={{
                      "&.Mui-expanded": {
                        minHeight: "auto",
                      },
                      "& .MuiAccordionSummary-content": {
                        margin: "20px 0",
                      },
                    }}
                  >
                    <Grid2 container alignItems="center" gap={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '4px', backgroundColor: '#EFF0FF', p: 0.75 }}>
                        <CustomIcon name="round-clock-icon" width="15" height="15" stroke="#4D44D8" />
                      </Box>
                      <Typography variant="fw700s14c3D4B5A" sx={{ color: "#2E2E2E", lineHeight: '20px' }}>
                        Time Configuration
                      </Typography>
                    </Grid2>
                  </AccordionSummary>
                  <AccordionDetails sx={{ paddingTop: 0 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {/* Date Filter */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: 'flex-start' }}>
                        <Typography
                          variant="fw500s12lh24c686868"
                          sx={{ mb: 1, fontSize: '13px', lineHeight: "20px" }}
                        >
                          Date:
                        </Typography>
                        <ChartCalendar
                          startDate={startDate}
                          endDate={endDate}
                          onDateChange={onDateChange}
                          disableAfterDate={analyticsCalendarEndDate()}
                          forcePresets={true}
                        />
                      </Box>

                      {/* Granularity */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: 'flex-start' }}>
                        <Typography
                          variant="fw500s12lh24c686868"
                          sx={{ mb: 1, fontSize: '13px', lineHeight: "20px" }}
                        >
                          Granularity:
                        </Typography>
                        <Grid2 container sx={{ background: '#F9F9F9', padding: '4px', borderRadius: '6px' }}>
                          {granularityOptions.map((option) => (
                            <PrimaryGranularityButton
                              key={option}
                              onClick={() => onGranularityChange(option)}
                              active={granularity === option}
                              sx={{ width: '90px' }}
                            >
                              <Typography textTransform="capitalize">{option}</Typography>
                            </PrimaryGranularityButton>
                          ))}
                        </Grid2>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    '&.Mui-expanded': {
                      mt: 0,
                      pb: 3
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardDoubleArrowDownIcon />}
                    sx={{
                      "&.Mui-expanded": {
                        minHeight: "auto",
                      },
                      "& .MuiAccordionSummary-content": {
                        margin: "20px 0",
                      },
                    }}
                  >
                    <Grid2 container alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 1 }}>
                      <Grid2 container alignItems="center" gap={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '4px', backgroundColor: '#EFF0FF', p: 0.75 }}>
                          <FilterIconSvg sx={{ color: '#4D44D8' }} />
                        </Box>
                        <Typography variant="fw700s14c3D4B5A" sx={{ color: "#2E2E2E", lineHeight: '20px' }}>
                          Filters
                        </Typography>
                      </Grid2>
                      <Typography
                        variant="fw500s12lh246D72F6"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetFilters();
                        }}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#4D44D8'
                          }
                        }}
                      >
                        Reset
                      </Typography>
                    </Grid2>
                  </AccordionSummary>
                  <AccordionDetails sx={{ paddingTop: 0 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                      {/* add all filter accordions here */}
                      {canUseGlobalAdvancedFilter && globalFilterConfig ? (
                        <GlobalFilter
                          allFleets={allFleets || []}
                          allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                          selectedFleetIds={selectedFleetIds || []}
                          selectedLocations={selectedHierarchicalLocation}
                          direction="vertical"
                          onChange={({ fleetIds, hierarchicalLocation }) => {
                            if (onHierarchicalLocationChange) {
                              onHierarchicalLocationChange(hierarchicalLocation);
                            }
                            // Business Segment and Customer Account both travel as
                            // fleetIds, so dropping it here would leave those two
                            // panels selectable but inert.
                            if (onFleetIdsChange) {
                              onFleetIdsChange(fleetIds);
                            }
                          }}
                        />
                      ) : (
                        <LocationFilter
                          locations={allOnboardingLocationsHierarchy}
                          selectedLocations={selectedHierarchicalLocation || []}
                          onLocationChange={(location) => {
                            if (onHierarchicalLocationChange) {
                              onHierarchicalLocationChange(location);
                            }
                          }}
                        />
                      )}
                      <ProjectFilter
                        selectedProjects={selectedProjects}
                        onProjectChange={setSelectedProjects}
                      />
                      <FaultCategoryFilter
                        selectedCategories={selectedCategories}
                        onCategoryChange={(categories) => {
                          setSelectedCategories(categories);
                          if (onAlertCategoriesChange) {
                            onAlertCategoriesChange(categories);
                          }
                        }}
                      />
                      <FaultNameFilter
                        selectedFaultNames={selectedFaultNames}
                        onFaultNameChange={setSelectedFaultNames}
                      />
                      {chartType === CHART_TYPES.AUTO_RESOLVED && (
                        <ResolutionDurationFilter
                          selectedResolutionDurations={selectedResolutionDurations}
                          onResolutionDurationChange={setSelectedResolutionDurations}
                        />
                      )}
                      <EntityAffectedFilter
                        selectedEntities={selectedEntities}
                        onEntityChange={setSelectedEntities}
                      />
                      <OperationalStatusFilter
                        selectedOperationalStatus={selectedOperationalStatus}
                        onOperationalStatusChange={(status) => {
                          setSelectedOperationalStatus(status);
                          if (onOperationalStatusChange) {
                            onOperationalStatusChange(status);
                          }
                        }}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ChartDetailsSettingsSidebar;
